import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Paper,
} from "@mui/material";

interface LoadingTimerDebugProps {
  loading: boolean;
  label: string;
  details: Record<string, unknown>;
}

const formatElapsed = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

const LoadingTimerDebug: React.FC<LoadingTimerDebugProps> = ({
  loading,
  label,
  details,
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      startRef.current = Date.now();
      setElapsedMs(0);
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      intervalRef.current = window.setInterval(() => {
        if (startRef.current) {
          setElapsedMs(Date.now() - startRef.current);
        }
      }, 200);
    } else if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loading]);

  const debugData = useMemo(
    () => ({
      label,
      loading,
      elapsedMs,
      elapsed: formatElapsed(elapsedMs),
      ...details,
    }),
    [details, elapsedMs, label, loading]
  );

  const debugText = useMemo(
    () => JSON.stringify(debugData, null, 2),
    [debugData]
  );

  // Debug: avoid logging on every elapsed tick (interval is 200ms).
  // Log when loading/details change instead, since console work can create UI lag.
  useEffect(() => {
    console.debug("[DEBUG] Loading timer", {
      label,
      loading,
      elapsed: formatElapsed(elapsedMs),
      ...details,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, label, loading]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(debugText);
      console.debug("[DEBUG] Copied loading debug info", { label });
    } catch (error) {
      console.error("[DEBUG] Copy failed", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          left: 16,
          bottom: 16,
          zIndex: 1400,
        }}
      >
        <Tooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography variant="subtitle2">{label}</Typography>
              <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                {loading ? "Loading" : "Loaded"} • {formatElapsed(elapsedMs)}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: 11,
                  lineHeight: 1.4,
                }}
              >
                {debugText}
              </Box>
            </Box>
          }
          arrow
          placement="top"
        >
          <Paper
            elevation={4}
            onClick={() => setOpen(true)}
            sx={{
              px: 1.5,
              py: 0.75,
              cursor: "pointer",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatElapsed(elapsedMs)}
            </Typography>
          </Paper>
        </Tooltip>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{label} debug info</DialogTitle>
        <DialogContent>
          <TextField
            value={debugText}
            fullWidth
            multiline
            minRows={8}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopy}>Copy</Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoadingTimerDebug;
